// Hotel-Edition capability in the client (PLAN.md §2, owner A0). Reads `edition` from the shared
// ['/api/companies/me'] query (React Query dedupes it with the sidebar and App). Menu and route locks use
// ONLY this hook, so they follow the server version: a server without the field means "no modules".
import { useQuery } from "@tanstack/react-query";
import {
  hasEditionModule, isEditionModule, isHotelEdition,
  type Edition, type EditionModule,
} from "@shared/edition";

export const COMPANY_ME_QUERY_KEY = ["/api/companies/me"] as const;

/** Capability refresh (PLAN §14 A-1, review he-a0 F1): hotel tabs re-read the capability every minute and
 *  on focus, so a module switched off (env cleared, code rollback) leaves an open page without needing a
 *  hotel API call to answer 403 first. Background tabs may be throttled by the browser; focus catches up.
 *  Agencies never poll (no behaviour change for them). */
export const EDITION_STALE_TIME_MS = 60_000;

/** Poll only while the cached company is a hotel. */
export function editionRefetchInterval(company: CompanyMe | undefined): number | false {
  return company && editionFromCompany(company).kind === "hotel" ? EDITION_STALE_TIME_MS : false;
}

/** loading / error → callers render the NEUTRAL view (no edition-specific menu, no module pages). */
export type EditionState =
  | { status: "loading"; edition: null }
  | { status: "error"; edition: null }
  | { status: "ready"; edition: Edition };

type CompanyMe = { dfyProfile?: string | null; edition?: Partial<Edition> | null };

/** The capability from a /api/companies/me body. Missing or malformed field (older server, PLAN §15 O-4)
 *  → no modules; the kind then falls back to dfyProfile so a hotel never sees the agency menu by accident. */
export function editionFromCompany(company: CompanyMe): Edition {
  const raw = company.edition;
  if (raw && (raw.kind === "hotel" || raw.kind === "agency")) {
    const modules = Array.isArray(raw.modules) ? raw.modules.filter(isEditionModule) : [];
    return raw.kind === "hotel" ? { kind: "hotel", modules } : { kind: "agency", modules: [] };
  }
  return { kind: isHotelEdition(company) ? "hotel" : "agency", modules: [] };
}

export function useEdition(): EditionState {
  const { data, isError } = useQuery<CompanyMe>({
    queryKey: COMPANY_ME_QUERY_KEY,
    staleTime: EDITION_STALE_TIME_MS,
    refetchInterval: (query) => editionRefetchInterval(query.state.data),
    refetchOnWindowFocus: (query) => editionRefetchInterval(query.state.data) !== false,
  });
  // Last good answer wins over a failed background refetch (a network blip must not bounce a module page);
  // the server still enforces every hotel API on its own.
  if (data) return { status: "ready", edition: editionFromCompany(data) };
  if (isError) return { status: "error", edition: null };
  return { status: "loading", edition: null };
}

export function useEditionModule(module: EditionModule): "loading" | "allowed" | "denied" {
  const state = useEdition();
  if (state.status === "loading") return "loading";
  if (state.status === "error") return "denied";
  return hasEditionModule(state.edition, module) ? "allowed" : "denied";
}

/** 403 { error: 'module_disabled' } from a hotel API (server/middleware/requireEditionModule.ts). */
export function isModuleDisabledError(error: unknown): boolean {
  const e = error as { status?: number; data?: { error?: unknown } } | undefined;
  return e?.status === 403 && e.data?.error === "module_disabled";
}
